/**
 * Scrape Repository
 * 
 * Extends BaseRepository with scrape-specific operations.
 * Common CRUD inherited from BaseRepository - no duplication!
 */

import { httpsCallable } from 'firebase/functions';
import { Scrape } from './Scrape';
import { BaseRepository } from './BaseRepository';

export class ScrapeRepository extends BaseRepository {
  constructor(db, functions) {
    super(db, functions, 'discovery_scrapes', Scrape);
  }

  // ========================================
  // SPECIALIZED FINDER METHODS
  // ========================================

  /**
   * Find scrapes by status
   * @param {string} status - Status to filter by
   * @returns {Promise<Scrape[]>} - Array of Scrape instances
   */
  async findByStatus(status) {
    return this.findAll({
      where: [['status', '==', status]],
      orderBy: 'createdAt',
      orderDirection: 'desc'
    });
  }

  /**
   * Find scrapes by scraper ID
   * @param {string} scraperId - Scraper ID (createdBy field)
   * @returns {Promise<Scrape[]>} - Array of Scrape instances
   */
  async findByScraperId(scraperId) {
    return this.findAll({
      where: [['createdBy', '==', scraperId]],
      orderBy: 'createdAt',
      orderDirection: 'desc'
    });
  }

  /**
   * Find scrapes by mailshot ID
   * @param {string} mailshotId - Mailshot ID
   * @returns {Promise<Scrape[]>} - Array of Scrape instances
   */
  async findByMailshotId(mailshotId) {
    return this.findAll({
      where: [['mailshotId', '==', mailshotId]],
      orderBy: 'createdAt',
      orderDirection: 'desc'
    });
  }

  // ========================================
  // OVERRIDDEN METHODS
  // ========================================

  /**
   * Create a new scrape (override to use jobId as document ID)
   * @param {Scrape} scrape - Scrape instance
   * @returns {Promise<string>} - Created scrape ID
   */
  async create(scrape) {
    // Validate before creating
    scrape.validate();
    
    const docData = scrape.toFirestore();
    
    // Use jobId as document ID
    await super.create(docData, scrape.jobId);
    
    return scrape.jobId;
  }

  // ========================================
  // CLOUD FUNCTION DELEGATIONS
  // ========================================

  /**
   * Merge scrapes (delegates to Cloud Function)
   *
   * Admins only: `mergeScrapes` deletes the source scrapes, refuses anyone but an admin, and
   * the page offers "Merge In" to admins only (6 Oct 2026).
   * @param {string[]} sourceIds - Source scrape IDs
   * @param {string} targetId - Target scrape ID
   * @returns {Promise<void>}
   */
  async merge(sourceIds, targetId) {
    const mergeScrapeFn = httpsCallable(this.functions, 'mergeScrapes');
    await mergeScrapeFn({ sourceIds, targetId });
  }

  /**
   * Split scrape (delegates to Cloud Function)
   * @param {string} scrapeId - Scrape ID to split
   * @param {Object} splitConfig - Configuration for split
   * @returns {Promise<void>}
   */
  async split(scrapeId, splitConfig) {
    const splitScrapeFn = httpsCallable(this.functions, 'splitScrape');
    await splitScrapeFn({ scrapeId, splitConfig });
  }

  /**
   * Recalculate scrape statistics (delegates to Cloud Function)
   * @param {string} scrapeId - Scrape ID
   * @returns {Promise<void>}
   */
  async recalculateStats(scrapeId) {
    const recalculateStatsFn = httpsCallable(this.functions, 'recalculateScrapeStats');
    await recalculateStatsFn({ scrapeId });
  }

  /**
   * Delete a scrape (override to delegate to Cloud Function)
   *
   * ⚠️ Never a plain document delete (25 Sep 2026). The inherited BaseRepository.delete
   * removed only the scrape document, so its properties kept a jobId pointing at nothing
   * and its mailshots kept it in scrapeIds — and any colleague could do it, although the
   * button is shown to admins only. `deleteScrape` is admin-only and clears both in one
   * transaction; the properties themselves stay. The rules now refuse client deletes.
   * (⚠️ "Admin-only" was not true of the function that actually ran until 6 Oct 2026: the
   * admin check had been put on an older copy that the export no longer pointed at.)
   * @param {string} id - Scrape ID
   * @returns {Promise<{success: boolean, propertiesUpdated: number, mailshotsUpdated: number}>}
   */
  async delete(id) {
    const deleteScrapeFn = httpsCallable(this.functions, 'deleteScrape');
    const result = await deleteScrapeFn({ scrapeId: id });
    return result.data;
  }

  /**
   * Cancel a running scrape (delegates to Cloud Function)
   * @param {string} scrapeId - Scrape ID
   * @returns {Promise<{cancelled: boolean, why?: string, status?: string}>}
   */
  async cancel(scrapeId) {
    // The callable writes the job's status in one transaction (functions/scrapes/cancelScrape.js);
    // the answer says whether it did, and if not, why.
    const cancelScrapeFn = httpsCallable(this.functions, 'cancelScrape');
    const res = await cancelScrapeFn({ scrapeId });
    return res && res.data;
  }

  /**
   * Archive a scrape (soft delete)
   * @param {string} scrapeId - Scrape ID
   * @returns {Promise<void>}
   */
  async archive(scrapeId) {
    await this.update(scrapeId, {
      archived: true,
      archivedAt: new Date().toISOString()
    });
  }
}
